import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Grid, InputAdornment, Stack, Switch, TextField, Typography } from '@mui/material';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import useConfirmationModal from '@hooks/useConfirmationModal';
import { ModalConfirmation } from '@components/Utilities/Modals/ModalConfirmation';
import BranchOfficesService from '@api/BranchOffices/BranchOfficeCtrl';
import InputsService from '@api/Inputs/InputCtrl';
import RecipesService from '@api/Recipes/RecipeCtrl';
import ModifierGroupsService from '@api/ModifierGroups/ModifierGroupCtrl';
import DishesService from '@api/Dishes/DishCtrl';
import { Input } from '@api/Inputs/Input';
import { CatalogSnapshot, getInputImpact, InputImpact } from '@api/Catalog/Availability';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';

interface ListAgotadosProps {
    branchOfficeId: string;
}

// "86" por sucursal: marcar un insumo como agotado oculta del menú 2.0 (web/kiosko/mesa,
// a partir de F3) los productos y opciones cuya receta lo consume. No toca stock ni bloquea
// la venta en POS — es visibilidad del menú, reversible al reabastecer.
const ListAgotados: React.FunctionComponent<ListAgotadosProps> = (props) => {
    const { branchOfficeId } = props;
    const branchOfficesService = useMemo(() => new BranchOfficesService(), []);
    const inputsService = useMemo(() => new InputsService(), []);
    const recipesService = useMemo(() => new RecipesService(), []);
    const modifierGroupsService = useMemo(() => new ModifierGroupsService(), []);
    const dishesService = useMemo(() => new DishesService(), []);

    const [inputs, setInputs] = useState<Input[]>([]);
    const [catalog, setCatalog] = useState<CatalogSnapshot>({ recipes: [], modifierGroups: [], dishes: [] });
    const [unavailableInputs, setUnavailableInputs] = useState<string[]>([]);
    const [filters, setFilters] = useState({ search: '' });
    const [pendingInput, setPendingInput] = useState<Input | undefined>(undefined);
    const modalConfirmation = useConfirmationModal();

    const unavailableSet = useMemo(() => new Set(unavailableInputs), [unavailableInputs]);

    // Impacto por insumo precalculado UNA vez por carga de catálogo (no por celda renderizada)
    const impactByInput = useMemo(() => {
        const map = new Map<string, InputImpact>();
        inputs.forEach((input) => {
            if (input.id) {
                map.set(input.id, getInputImpact(input.id, catalog));
            }
        });
        return map;
    }, [inputs, catalog]);

    const getData = async () => {
        showLoader();
        try {
            const [dataBranchOffice, dataInputs, dataRecipes, dataModifierGroups, dataDishes] = await Promise.all([
                branchOfficesService.getBranchOffice(branchOfficeId),
                inputsService.getInputs(),
                recipesService.getRecipes(),
                modifierGroupsService.getModifierGroups(),
                dishesService.getDishes()
            ]);
            setUnavailableInputs(dataBranchOffice.unavailableInputs || []);
            setInputs(dataInputs);
            setCatalog({ recipes: dataRecipes, modifierGroups: dataModifierGroups, dishes: dataDishes });
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
    }, [branchOfficeId]);

    const applyToggle = async (input: Input, unavailable: boolean) => {
        if (!input.id) return;
        showLoader();
        try {
            await branchOfficesService.setInputUnavailability(branchOfficeId, input.id, input.name, unavailable);
            // Refresco local optimista + relectura del doc (otro dispositivo pudo mover la lista)
            const dataBranchOffice = await branchOfficesService.getBranchOffice(branchOfficeId);
            setUnavailableInputs(dataBranchOffice.unavailableInputs || []);
            showAlertMessage(
                unavailable ? `"${input.name}" marcado como agotado` : `"${input.name}" disponible de nuevo`
            );
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onToggle = (input: Input, unavailable: boolean) => {
        if (!unavailable) {
            // Reabrir no necesita confirmación
            void applyToggle(input, false);
            return;
        }
        // Marcar agotado: confirmar mostrando el impacto en el menú 2.0
        const impact = impactByInput.get(input.id!) || { recipes: [], dishes: [], options: [] };
        const parts: string[] = [];
        if (impact.dishes.length > 0) {
            parts.push(`Productos que se ocultarán: ${impact.dishes.map((dish) => dish.name).join(', ')}.`);
        }
        if (impact.options.length > 0) {
            parts.push(
                `Opciones que se ocultarán: ${impact.options
                    .map(({ group, option }) => `${option.name} (${group.name})`)
                    .join(', ')}.`
            );
        }
        if (parts.length === 0) {
            parts.push(
                impact.recipes.length > 0
                    ? `Lo usan ${impact.recipes.length} receta(s), pero ningún producto del menú depende de él todavía.`
                    : 'Ningún producto del menú depende de este insumo todavía.'
            );
        }
        setPendingInput(input);
        modalConfirmation.setModalData(`¿Marcar "${input.name}" como agotado?`, parts.join(' '));
    };

    const columns = useMemo<ResponsiveGridColDef<Input>[]>(
        () => [
            {
                field: 'category',
                flex: 1,
                showInMobile: false,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Categoría
                    </Typography>
                ),
                valueGetter: ({ row }) => row.inputCategoryData?.name || 'Sin categoría',
                renderCell: ({ row }) => <Typography>{row.inputCategoryData?.name || 'Sin categoría'}</Typography>
            },
            {
                field: 'name',
                flex: 1.5,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Insumo
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.name}</Typography>
            },
            {
                field: 'impact',
                flex: 1.5,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Impacto en menú 2.0
                    </Typography>
                ),
                renderCell: ({ row }) => {
                    const impact = impactByInput.get(row.id!) || { recipes: [], dishes: [], options: [] };
                    if (impact.dishes.length === 0 && impact.options.length === 0) {
                        return (
                            <Typography variant="body2" color="text.secondary">
                                Sin dependencias aún
                            </Typography>
                        );
                    }
                    return (
                        <Stack direction="row" spacing={1}>
                            {impact.dishes.length > 0 && (
                                <Chip size="small" variant="outlined" label={`${impact.dishes.length} producto(s)`} />
                            )}
                            {impact.options.length > 0 && (
                                <Chip size="small" variant="outlined" label={`${impact.options.length} opción(es)`} />
                            )}
                        </Stack>
                    );
                }
            },
            {
                field: 'status',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Estado
                    </Typography>
                ),
                renderCell: ({ row }) =>
                    unavailableSet.has(row.id!) ? (
                        <Chip size="small" color="error" label="Agotado" />
                    ) : (
                        <Chip size="small" color="success" label="Disponible" />
                    )
            },
            {
                field: 'actions',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => <SettingsIcon sx={{ color: '#809FB8' }} />,
                renderCell: ({ row }) => (
                    <Switch
                        color="error"
                        checked={unavailableSet.has(row.id!)}
                        onChange={(e) => onToggle(row, e.target.checked)}
                    />
                )
            }
        ],
        [unavailableSet, impactByInput]
    );

    return (
        <Stack height="100%" spacing={2}>
            <Box>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={9}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
                            <Typography variant="body2" color="text.secondary">
                                {unavailableInputs.length === 0
                                    ? 'Sin insumos agotados en esta sucursal'
                                    : `${unavailableInputs.length} insumo(s) agotado(s):`}
                            </Typography>
                            {unavailableInputs.map((inputId) => {
                                const input = inputs.find((i) => i.id === inputId);
                                return (
                                    <Chip
                                        key={inputId}
                                        size="small"
                                        color="error"
                                        label={input?.name || inputId}
                                        onDelete={input ? () => applyToggle(input, false) : undefined}
                                    />
                                );
                            })}
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Buscar insumo"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
            <ResponsiveDataGrid searchValue={filters.search} rows={inputs} columns={columns} />
            <ModalConfirmation
                modalConfirmation={modalConfirmation}
                onConfirmation={() => {
                    if (pendingInput) {
                        void applyToggle(pendingInput, true);
                    }
                }}
            />
        </Stack>
    );
};

export default ListAgotados;
